if (!customElements.get('product-tabs-slider')) {
  customElements.define(
    'product-tabs-slider',
    class ProductTabsSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.selectors = {
          sliderWrapper: '.product-tabs__items',
        };
        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.sectionId = this.dataset.sectionId;
        this.gridList = this.closest('grid-list');
        this.tabPanel = this.closest('.tabs__content');
        this.tabsComponent = this.closest('tabs-component');
        this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

        this.enableSlider = this.dataset.enableSlider === 'true';
        this.enableSwipeMobile = this.dataset.enableSwipeMobile === 'true';
        this.items = parseInt(this.dataset.items);
        this.tabletItems = parseInt(this.dataset.tabletItems);

        this.sliderInstance = false;
        this.swipeMobileInitialized = false;

        if (!this.enableSlider && !this.enableSwipeMobile) return;

        if (this.tabsComponent) {
          this.onTabChangeHandler = this.onTabChange.bind(this);
          this.tabsComponent.addEventListener('tabChange', this.onTabChangeHandler);
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        this.onMediaChangeHandler = this.onMediaChange.bind(this);
        mql.addEventListener('change', this.onMediaChangeHandler);
        this.onMediaChange();
      }

      disconnectedCallback() {
        if (this.tabsComponent && this.onTabChangeHandler) {
          this.tabsComponent.removeEventListener('tabChange', this.onTabChangeHandler);
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.removeEventListener('change', this.onMediaChangeHandler);

        this.destroySlider();
        this.destroySwipeMobile();
      }

      isPanelVisible() {
        return this.tabPanel && !this.tabPanel.hidden;
      }

      onTabChange() {
        if (this.isPanelVisible()) {
          requestAnimationFrame(() => this.onMediaChange());
        } else {
          this.destroySlider();
          this.destroySwipeMobile();
        }
      }

      onMediaChange() {
        if (!this.isPanelVisible()) return;

        if (FoxTheme.config.mqlMobile) {
          this.destroySlider();
          if (this.enableSwipeMobile) {
            this.initSwipeMobile();
          } else {
            this.destroySwipeMobile();
          }
        } else {
          this.destroySwipeMobile();
          if (this.enableSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (!this.enableSlider || !this.isPanelVisible()) return;

        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.update();
          this.calcNavButtonsPosition();
          return;
        }

        const desktopControls = this.gridList?.querySelector('.swiper-controls--desktop');
        const columnGap = window.getComputedStyle(this.sliderWrapper).getPropertyValue('--column-gap');
        const spaceBetween = parseFloat(columnGap.replace('rem', '')) * 10;

        this.sliderOptions = {
          slidesPerView: this.tabletItems > 3 ? 3 : parseInt(this.tabletItems),
          spaceBetween: spaceBetween,
          navigation: {
            nextEl: desktopControls?.querySelector('.swiper-button-next'),
            prevEl: desktopControls?.querySelector('.swiper-button-prev'),
          },
          pagination: false,
          breakpoints: {
            1280: {
              slidesPerView: parseInt(this.items),
            },
          },
          loop: true,
          threshold: 2,
        };

        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions);
        this.sliderInstance.init();

        this.calcNavButtonsPosition();
        this.fixQuickviewDuplicate();
      }

      destroySlider() {
        this.classList.remove(this.classes.swiper);
        if (this.sliderWrapper) {
          this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
          this.sliderWrapper.classList.add(this.classes.grid);
        }
        if (typeof this.sliderInstance !== 'object') return;
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }

      initSwipeMobile() {
        if (!this.enableSwipeMobile || !FoxTheme.config.mqlMobile || !this.isPanelVisible()) return;

        this.mobileControls = this.gridList?.querySelector('.swiper-controls--mobile');
        if (!this.mobileControls) return;

        if (this.swipeMobileInitialized) {
          this.updateSwipeButtonState();
          this.calcNavButtonsPosition();
          return;
        }

        this.prevButton = this.mobileControls.querySelector('.swiper-button-prev');
        this.nextButton = this.mobileControls.querySelector('.swiper-button-next');

        this.onSwipePrev = () => this.scrollSwipe(-1);
        this.onSwipeNext = () => this.scrollSwipe(1);
        this.onSwipeScroll = () => this.updateSwipeButtonState();

        this.prevButton?.addEventListener('click', this.onSwipePrev);
        this.nextButton?.addEventListener('click', this.onSwipeNext);
        this.addEventListener('scroll', this.onSwipeScroll, { passive: true });

        this.swipeMobileInitialized = true;
        this.updateSwipeButtonState();
        this.calcNavButtonsPosition();
      }

      destroySwipeMobile() {
        if (!this.swipeMobileInitialized) return;

        this.prevButton?.removeEventListener('click', this.onSwipePrev);
        this.nextButton?.removeEventListener('click', this.onSwipeNext);
        this.removeEventListener('scroll', this.onSwipeScroll);

        this.swipeMobileInitialized = false;
      }

      getSwipeScrollStep() {
        const firstItem = this.sliderWrapper?.querySelector('.f-column');
        if (!firstItem) return 0;

        const styles = window.getComputedStyle(this.sliderWrapper);
        const gap = parseFloat(styles.columnGap) || parseFloat(styles.gap) || 0;

        return firstItem.offsetWidth + gap;
      }

      scrollSwipe(direction) {
        const step = this.getSwipeScrollStep();
        if (!step) return;

        const offset = direction * step * (FoxTheme.config.isRTL ? -1 : 1);
        this.scrollBy({ left: offset, behavior: 'smooth' });
      }

      updateSwipeButtonState() {
        if (!this.prevButton || !this.nextButton) return;

        const maxScroll = this.scrollWidth - this.clientWidth;
        if (maxScroll <= 0) {
          this.prevButton.disabled = true;
          this.nextButton.disabled = true;
          return;
        }

        const scrollPos = Math.abs(this.scrollLeft);
        this.prevButton.disabled = scrollPos <= 1;
        this.nextButton.disabled = scrollPos >= maxScroll - 1;
      }

      calcNavButtonsPosition() {
        if (this.dataset.calcButtonPosition !== 'true') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        if (firstMedia && firstMedia.clientHeight > 0 && this.gridList) {
          this.gridList.style.setProperty(
            '--swiper-navigation-top-offset',
            parseInt(firstMedia.clientHeight) / 2 + 'px'
          );
        }
      }

      fixQuickviewDuplicate() {
        let modalIds = [];
        Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
          const modalID = modal.getAttribute('id');
          if (modalIds.includes(modalID)) {
            modal.remove();
          } else {
            modalIds.push(modalID);
          }
        });
      }
    }
  );
}
